import { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isWithinInterval } from 'date-fns';
import type { CRMInteraction, CRMStakeholder, CRMFollowUp } from '@/lib/crm/types';
import { getInteractionTypeColor } from '@/lib/stakeholders/utils';

type TimeRange = 'day' | 'week' | 'month' | 'all';

interface StakeholderEventsListProps {
  stakeholders: CRMStakeholder[];
  interactions: CRMInteraction[];
  followUps: CRMFollowUp[];
  selectedDate?: Date;
  defaultTimeRange?: TimeRange;
  onSelectedDateChange?: (date: Date | undefined) => void;
}

export function StakeholderEventsList({ 
  stakeholders, 
  interactions, 
  followUps, 
  selectedDate,
  defaultTimeRange = 'day',
  onSelectedDateChange 
}: StakeholderEventsListProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>(defaultTimeRange);
  
  // Auto-switch to 'day' view when selectedDate changes from calendar click
  useEffect(() => {
    if (selectedDate && timeRange !== 'day') {
      setTimeRange('day');
    }
  }, [selectedDate]);
  
  const events = useMemo(() => {
    const result: any[] = [];
    const now = new Date();
    
    // Determine date range based on timeRange state
    let startDate: Date;
    let endDate: Date;
    
    switch (timeRange) {
      case 'day':
        if (!selectedDate) return [];
        startDate = new Date(selectedDate);
        startDate.setHours(0, 0, 0, 0);
        endDate = new Date(selectedDate);
        endDate.setHours(23, 59, 59, 999);
        break;
      case 'week':
        startDate = startOfWeek(now, { weekStartsOn: 1 }); // Monday
        endDate = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'month':
        startDate = startOfMonth(now);
        endDate = endOfMonth(now);
        break;
      case 'all':
        startDate = new Date(0); // Unix epoch
        endDate = new Date(2100, 0, 1); // Far future
        break;
    }
    
    // Filter follow-ups within date range from followUps array
    followUps.forEach(f => {
      try {
        const followUpDate = new Date(f.follow_up_date);
        if (isWithinInterval(followUpDate, { start: startDate, end: endDate })) {
          const stakeholder = stakeholders.find(s => s.id === f.stakeholder_id);
          if (stakeholder) {
            result.push({
              type: 'follow-up',
              stakeholder,
              date: f.follow_up_date,
              content: f.notes || 'Không có ghi chú',
              followUp: f
            });
          }
        }
      } catch (e) {
        // Invalid date, skip
      }
    });
    
    // Filter interactions within date range
    interactions.forEach(i => {
      try {
        const interactionDate = new Date(i.date);
        if (isWithinInterval(interactionDate, { start: startDate, end: endDate })) {
          const stakeholder = stakeholders.find(s => s.id === i.stakeholder_id || s.id === i.stakeholderId);
          result.push({
            type: 'interaction',
            stakeholder,
            interaction: i,
            date: i.date
          });
        }
      } catch (e) {
        // Invalid date, skip
      }
    });
    
    // Sort by date (newest first for 'all', chronological for time-bound)
    return result.sort((a, b) => {
      const dateA = new Date(a.date).getTime();
      const dateB = new Date(b.date).getTime();
      return timeRange === 'all' ? dateB - dateA : dateA - dateB;
    });
  }, [stakeholders, interactions, followUps, selectedDate, timeRange]);
  
  return (
    <Card className="h-full flex flex-col">
      <CardHeader className="pb-3">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="text-sm font-semibold">
            {timeRange === 'day' && selectedDate && format(selectedDate, 'dd/MM')}
            {timeRange === 'week' && 'Tuần này'}
            {timeRange === 'month' && 'Tháng này'}
            {timeRange === 'all' && 'Tất cả'}
          </div>
          
          <ToggleGroup 
            type="single" 
            value={timeRange} 
            onValueChange={(value) => value && setTimeRange(value as TimeRange)}
            className="border rounded-lg"
          >
            <ToggleGroupItem value="day" aria-label="Today" className="px-2 py-1 text-xs">
              Ngày
            </ToggleGroupItem>
            <ToggleGroupItem value="week" aria-label="This week" className="px-2 py-1 text-xs">
              Tuần
            </ToggleGroupItem>
            <ToggleGroupItem value="month" aria-label="This month" className="px-2 py-1 text-xs">
              Tháng
            </ToggleGroupItem>
            <ToggleGroupItem value="all" aria-label="All events" className="px-2 py-1 text-xs">
              Tất cả
            </ToggleGroupItem>
          </ToggleGroup>
        </div>
      </CardHeader>
      <CardContent className="p-3 pt-0 flex-1">
        <ScrollArea className="h-[300px] sm:h-[400px] md:h-[500px]">
          {events.length === 0 ? (
            <p className="text-muted-foreground text-sm">
              {timeRange === 'day' && 'Không có sự kiện nào trong ngày này'}
              {timeRange === 'week' && 'Không có sự kiện nào trong tuần này'}
              {timeRange === 'month' && 'Không có sự kiện nào trong tháng này'}
              {timeRange === 'all' && 'Chưa có sự kiện nào'}
            </p>
          ) : (
            <div className="space-y-2">
              {events.map((event, index) => (
                <div key={index} className="p-3 border rounded-lg hover:bg-muted/50 transition-colors">
                  {/* Row 1: Type badge with I/F corner + Stakeholder + Date */}
                  <div className="flex items-center gap-2 flex-wrap text-sm sm:text-base">
                    <div className="relative inline-block">
                      <Badge 
                        className={
                          event.type === 'follow-up' 
                            ? getInteractionTypeColor(event.followUp?.type || 'Meeting')
                            : getInteractionTypeColor(event.interaction?.type)
                        }
                      >
                        {event.type === 'follow-up' 
                          ? (event.followUp?.type || 'Follow-up')
                          : event.interaction?.type
                        }
                      </Badge>
                      <span className="absolute -top-1 -right-1 bg-primary text-primary-foreground text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
                        {event.type === 'interaction' ? 'I' : 'F'}
                      </span>
                    </div>
                    
                    <span className="font-medium">{event.stakeholder?.name || 'Unknown'}</span>
                    
                    <span className="text-xs text-muted-foreground ml-auto">
                      {format(new Date(event.date), timeRange === 'day' ? 'HH:mm' : 'dd/MM/yyyy HH:mm')}
                    </span>
                  </div>
                  
                  {/* Row 2: Subject + Notes on same line */}
                  <div className="mt-2 text-sm">
                    {event.type === 'follow-up' && event.followUp && (
                      <>
                        {event.followUp.subject && (
                          <span className="font-medium text-foreground">{event.followUp.subject}</span>
                        )}
                        {event.followUp.subject && event.followUp.notes && (
                          <span className="text-muted-foreground"> - </span>
                        )}
                        {event.followUp.notes && (
                          <span className="text-muted-foreground">{event.followUp.notes}</span>
                        )}
                      </>
                    )}
                    
                    {event.interaction && (
                      <>
                        <span className="font-medium text-foreground">{event.interaction.subject}</span>
                        {event.interaction.subject && event.interaction.notes && (
                          <span className="text-muted-foreground"> - </span>
                        )}
                        {event.interaction.notes && (
                          <span className="text-muted-foreground">{event.interaction.notes}</span>
                        )}
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </CardContent>
    </Card>
  );
}
